import React, { useEffect, useRef, useState } from 'react'

export type CustomSelectOption = {
  value: string | number
  label: string
}

interface Props {
  value: string | number | null
  onChange: (value: string) => void
  options: CustomSelectOption[]
  placeholder?: string
  clearable?: boolean
  title?: string
  name?: string
  disabled?: boolean
  className?: string
}

const CustomSelect: React.FC<Props> = ({
  value,
  onChange,
  options,
  placeholder = '選択してください',
  clearable = false,
  title,
  name,
  disabled = false,
  className,
}) => {
  const [isOpen, setOpen] = useState(false)
  const previousOverflowRef = useRef<string>('')
  const listRef = useRef<HTMLUListElement>(null)
  const [canScrollUp, setCanScrollUp] = useState(false)
  const [canScrollDown, setCanScrollDown] = useState(false)

  useEffect(() => {
    if (isOpen) {
      previousOverflowRef.current = document.body.style.overflow
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = previousOverflowRef.current
    }
    return () => {
      document.body.style.overflow = previousOverflowRef.current
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return undefined
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [isOpen])

  useEffect(() => {
    const el = listRef.current
    if (!el) return undefined
    const update = () => {
      setCanScrollUp(el.scrollTop > 1)
      setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1)
    }
    update()
    el.addEventListener('scroll', update, { passive: true })
    let ro: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update)
      ro.observe(el)
    }
    return () => {
      el.removeEventListener('scroll', update)
      ro?.disconnect()
    }
  }, [isOpen, options.length, clearable])

  useEffect(() => {
    if (!isOpen) return
    const list = listRef.current
    if (!list) return
    const selectedEl = list.querySelector<HTMLElement>('.cs-option-selected')
    if (!selectedEl) {
      list.scrollTop = 0
      return
    }
    const targetTop = selectedEl.offsetTop - (list.clientHeight - selectedEl.clientHeight) / 2
    list.scrollTop = Math.max(0, targetTop)
  }, [isOpen])

  const selectedOption = options.find((o) => String(o.value) === String(value))
  const triggerLabel = selectedOption ? selectedOption.label : placeholder
  const isPlaceholder = !selectedOption

  const handleSelect = (selected: string) => {
    onChange(selected)
    setOpen(false)
  }

  const handleOptionKey = (e: React.KeyboardEvent, selected: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleSelect(selected)
    }
  }

  const triggerClassName = ['cs-trigger', className].filter(Boolean).join(' ')

  return (
    <>
      <button
        type="button"
        className={triggerClassName}
        onClick={() => !disabled && setOpen(true)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        data-cs-name={name}
      >
        <span className={`cs-trigger-label${isPlaceholder ? ' cs-trigger-placeholder' : ''}`}>
          {triggerLabel}
        </span>
        <span className="cs-trigger-caret" aria-hidden="true">
          ▼
        </span>
      </button>
      {name && <input type="hidden" name={name} value={value == null ? '' : String(value)} />}
      <div
        className={`cs-backdrop${isOpen ? ' cs-backdrop-open' : ''}`}
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />
      <div
        className={`cs-sheet${isOpen ? ' cs-sheet-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title || placeholder}
      >
        <div className="cs-sheet-header">
          <span>{title || placeholder}</span>
          <button
            type="button"
            className="cs-sheet-close"
            onClick={() => setOpen(false)}
            aria-label="閉じる"
          >
            &times;
          </button>
        </div>
        <div className="cs-option-list-wrapper">
          <ul className="cs-option-list" role="listbox" ref={listRef}>
            {clearable && (
              <li
                className="cs-option-item cs-option-clear"
                role="option"
                aria-selected={isPlaceholder}
                tabIndex={0}
                onClick={() => handleSelect('')}
                onKeyDown={(e) => handleOptionKey(e, '')}
              >
                {/* mark span は他 option との左 indent (0.9em) を揃えるために空で残す */}
                <span className="cs-option-mark" aria-hidden="true" />
                選択を解除
              </li>
            )}
            {options.map((opt) => {
              const isSelected = String(opt.value) === String(value)
              const itemClassName = `cs-option-item${isSelected ? ' cs-option-selected' : ''}`
              return (
                <li
                  key={opt.value}
                  className={itemClassName}
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={0}
                  onClick={() => handleSelect(String(opt.value))}
                  onKeyDown={(e) => handleOptionKey(e, String(opt.value))}
                >
                  {/* ::before content だと Chromium が accessible name に含めてしまい
                       getByRole('option', { name, exact: true }) が崩れるため JSX で出す */}
                  <span className="cs-option-mark" aria-hidden="true">
                    {isSelected ? '✓' : ''}
                  </span>
                  {opt.label}
                </li>
              )
            })}
          </ul>
          <div
            className={`cs-scroll-hint cs-scroll-hint-top${canScrollUp ? ' cs-scroll-hint-visible' : ''}`}
            aria-hidden="true"
          >
            <span className="cs-scroll-hint-arrow">▲</span>
          </div>
          <div
            className={`cs-scroll-hint cs-scroll-hint-bottom${canScrollDown ? ' cs-scroll-hint-visible' : ''}`}
            aria-hidden="true"
          >
            <span className="cs-scroll-hint-arrow">▼</span>
          </div>
        </div>
      </div>
    </>
  )
}

export default CustomSelect
